// @ts-nocheck
// Business logic for surfacing ComposedForecasts on the asset dashboard.
// Kept out of components per CLAUDE.md (utils for business logic; components
// for lifecycle + rendering).

import type { CombinedPreviewData } from "./composedForecastUtils";
import { COMPOSED_FORECAST_ICON_MAP } from "../components/utils/composedForecastIconOptions";

/** A single ComposedForecastAssets join row, trimmed to what the diff needs. */
export interface ComposedForecastAssetLinkRow {
  id: string;
  composedForecastID: string;
  icon?: string | null;
  isDefault?: boolean | null;
  // composedForecast nested object also present at runtime but unused here.
}

/** A row in the asset modal's picker — one entry per checked forecast. */
export interface SelectedComposedForecast {
  composedForecastID: string;
  icon: string | null;
  /** The asset dashboard opens on this forecast. At most one per asset. */
  isDefault?: boolean;
}

/**
 * Pure diff between the currently-selected composed forecasts in the picker
 * and the join rows that already exist for an asset. Used by
 * `syncComposedForecastAssetLinks` to decide which rows to create / delete /
 * update (icon / default changes preserve the row id and createdAt).
 */
export function diffComposedForecastLinks(
  selectedForecasts: SelectedComposedForecast[],
  existingLinks: ComposedForecastAssetLinkRow[]
): {
  toCreate: SelectedComposedForecast[];
  toDelete: ComposedForecastAssetLinkRow[];
  toUpdate: { id: string; icon: string | null; isDefault: boolean }[];
} {
  const existingByForecast = new Map(
    existingLinks.map((r) => [r.composedForecastID, r])
  );
  const selectedSet = new Set(
    selectedForecasts.map((s) => s.composedForecastID)
  );

  const toCreate = selectedForecasts.filter(
    (s) => !existingByForecast.has(s.composedForecastID)
  );
  const toDelete = existingLinks.filter(
    (r) => !selectedSet.has(r.composedForecastID)
  );
  const norm = (v: string | null | undefined) => v ?? null;
  const toUpdate = selectedForecasts
    .filter((s) => {
      const existing = existingByForecast.get(s.composedForecastID);
      if (!existing) return false;
      return norm(s.icon) !== norm(existing.icon) || !!s.isDefault !== !!existing.isDefault;
    })
    .map((s) => ({
      id: existingByForecast.get(s.composedForecastID).id,
      icon: s.icon ?? null,
      isDefault: !!s.isDefault,
    }));

  return { toCreate, toDelete, toUpdate };
}

/**
 * Bridge from a stored icon-name string (a COMPOSED_FORECAST_ICON_OPTIONS
 * value key) to a React component the forecast dropdown can render. Falls
 * back to the provided `fallbackIcon` when no name is set or the name isn't
 * in the registry — preserves the legacy HurricaneIcon look for link rows
 * created before per-link icons existed.
 */
export function iconNameToReactComponent(
  name: string | null | undefined,
  fallbackIcon: any
) {
  if (!name) return fallbackIcon;
  return COMPOSED_FORECAST_ICON_MAP[name] || fallbackIcon;
}

/**
 * Build forecast-dropdown schema entries (keyed `composed-${forecastId}`)
 * from an asset's nested `linkedComposedForecasts` rows. Mirrors the
 * `processed-*` block in `buildForecastSchema`.
 *
 * `fallbackIcon` is used when the join row has no `icon` value — typically
 * the legacy HurricaneIcon so existing links keep their look. A composed
 * forecast renders one combined chart, so a single no-op "Combined"
 * variation suffices.
 */
export function buildComposedForecastSchemaEntries(
  asset: any,
  fallbackIcon: any
): Record<string, { name: string; Icon: any; variations: { name: string; key: string }[] }> {
  const entries: Record<string, any> = {};
  const items = asset?.linkedComposedForecasts?.items ?? [];

  items.forEach((link: any) => {
    const cf = link?.composedForecast;
    if (!cf?.id) return;
    if (cf.isActive === false) return; // Skip explicitly-inactive forecasts.
    entries[`composed-${cf.id}`] = {
      name: cf.name || "Composed Forecast",
      Icon: iconNameToReactComponent(link?.icon, fallbackIcon),
      variations: [{ name: "Combined", key: "Combined" }],
    };
  });

  return entries;
}

/**
 * The forecast-dropdown key (`composed-${id}`) the asset dashboard should open
 * on: the active link marked default, else the first active link, else null.
 * A linked composed forecast always beats a V1 processed model.
 */
export function defaultComposedForecastKey(asset: any): string | null {
  const active = (asset?.linkedComposedForecasts?.items ?? []).filter(
    (link: any) => link?.composedForecast?.id && link.composedForecast.isActive !== false
  );
  const chosen = active.find((link: any) => link.isDefault) ?? active[0];
  return chosen ? `composed-${chosen.composedForecast.id}` : null;
}

/**
 * Resolve the linked ComposedForecast object for an `activeForecast` key of
 * shape `composed-${id}`. Returns `null` if not found.
 */
export function resolveComposedForecast(asset: any, activeForecast: string): any | null {
  if (!activeForecast?.startsWith("composed-")) return null;
  const forecastId = activeForecast.slice("composed-".length);
  const items = asset?.linkedComposedForecasts?.items ?? [];
  for (const link of items) {
    if (link?.composedForecast?.id === forecastId) return link.composedForecast;
  }
  return null;
}

/**
 * Flatten a CombinedPreviewData object into time-indexed rows for the table
 * view and CSV export.
 *
 * The base-model series is NOT included here — the table/CSV currently
 * surface scalar series only, and the base model is graph-only (percentile
 * bands). Each overlay line contributes one column keyed by its `name`.
 *
 * Returns rows sorted by timestamp ascending.
 */
export function flattenComposedForecast(
  combinedData: CombinedPreviewData | null | undefined
): Array<Record<string, any>> {
  if (!combinedData) return [];
  const { overlayLines } = combinedData;
  if (!overlayLines?.length) return [];

  // Bucket all overlay points by timestamp string so rows align.
  const byTs: Map<string, Record<string, any>> = new Map();

  overlayLines.forEach((line) => {
    line.records?.forEach((rec) => {
      const ts =
        rec.timestamp instanceof Date
          ? rec.timestamp.toISOString()
          : String(rec.timestamp);
      let row = byTs.get(ts);
      if (!row) {
        row = { timestamp: ts };
        byTs.set(ts, row);
      }
      row[line.name] = rec.value;
    });
  });

  return Array.from(byTs.values()).sort((a, b) =>
    String(a.timestamp).localeCompare(String(b.timestamp))
  );
}
